/**
 * Сведение для финального экрана игры. Эндпоинт тот же, что у редактора:
 * POST /api/projects/{id}/render (docs/API.md). Прогресс приходит по WS,
 * поллинг — фолбэк на случай, если сокет не поднялся.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import * as api from '../api/client'
import { describeError } from '../api/errors'
import { reportRequestFailure } from '../api/serverStatus'
import { ProjectSocket, projectWsUrl } from '../api/ws'
import type { Render } from '../api/types'

export type RenderController = {
  render: Render | null
  starting: boolean
  error: string | null
  start: (options: api.RenderOptions) => void
  reset: () => void
}

export function useProjectRender(projectId: string): RenderController {
  const [render, setRender] = useState<Render | null>(null)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const renderRef = useRef<Render | null>(null)
  const mountedRef = useRef(true)
  renderRef.current = render

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const socket = new ProjectSocket(projectWsUrl(projectId))
    socket.connect()
    const off = socket.onEvent((event) => {
      const current = renderRef.current
      if (!current) return
      if (!('renderId' in event) || event.renderId !== current.id) return
      if (event.type === 'render.progress') {
        setRender({ ...current, status: 'running', progress: event.progress })
      } else if (event.type === 'render.done') {
        void api
          .getRender(current.id)
          .then((next) => {
            if (mountedRef.current) setRender(next)
          })
          .catch(() => {
            if (mountedRef.current) setRender({ ...current, status: 'done', progress: 1 })
          })
      } else if (event.type === 'render.error') {
        setRender({ ...current, status: 'error', error: event.error })
      }
    })
    return () => {
      off()
      socket.dispose()
    }
  }, [projectId])

  useEffect(() => {
    if (!render || (render.status !== 'pending' && render.status !== 'running')) return
    const id = render.id
    let cancelled = false
    const timer = window.setInterval(() => {
      void api
        .getRender(id)
        .then((next) => {
          if (!cancelled && mountedRef.current) setRender(next)
        })
        .catch(() => undefined)
    }, 2000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [render])

  const start = useCallback(
    (options: api.RenderOptions): void => {
      setStarting(true)
      setError(null)
      void api
        .startRender(projectId, options)
        .then((created) => {
          if (mountedRef.current) setRender(created)
        })
        .catch((err: unknown) => {
          reportRequestFailure(err)
          if (mountedRef.current) setError(describeError(err))
        })
        .finally(() => {
          if (mountedRef.current) setStarting(false)
        })
    },
    [projectId],
  )

  const reset = useCallback((): void => {
    setRender(null)
    setError(null)
  }, [])

  return { render, starting, error, start, reset }
}
